/**
 * Account-pair API schemas — the parent entity of the hedge hierarchy.
 *
 *   account_pairs  (this file)
 *     └── pairs    (symbol hedges — see api/pairs.ts)
 *           └── trades / audit_log
 *
 * An account_pair is the permanent relationship between two specific
 * accounts that together form a hedge. Operator picks the symbols the
 * account_pair is allowed to trade (`symbolAllowlist`); Haiku selects
 * from that allowlist at runtime. Every `pairs` row (a symbol hedge) is
 * a child of exactly one account_pair.
 */

import { z } from "zod";
import { PairModeZ } from "../domain/pair";
import { PairListItemZ } from "./pairs";

/** Snapshot the bot's worker captured just before context.close() ran
 *  on the last sleep. Lets the dashboard show "Asleep with these
 *  positions" instead of going dark on a sleeping leg. */
export const SleepSnapshotZ = z.object({
  equity: z.number().nullable(),
  balance: z.number().nullable(),
  marginLevel: z.number().nullable(),
  openPnl: z.number(),
  closedPnl: z.number().nullable(),
  drawdownUsed: z.number().nullable(),
  dailyUsed: z.number().nullable(),
  openPositions: z.array(
    z.object({
      ticket: z.number().int(),
      symbol: z.string(),
      side: z.enum(["buy", "sell"]),
      lots: z.number(),
      entry: z.number(),
      stopLoss: z.number().nullable(),
      takeProfit: z.number().nullable(),
      openPnl: z.number(),
    }),
  ),
  positionCount: z.number().int().nonnegative(),
  protectedCount: z.number().int().nonnegative(),
  capturedAt: z.number().int(),
});
export type SleepSnapshot = z.infer<typeof SleepSnapshotZ>;

/** Side-agnostic account summary shown in account_pair cards. */
export const AccountPairSideZ = z.object({
  accountId: z.string(),
  slug: z.string(),
  firmId: z.string(),
  firmName: z.string(),
  firmLogo: z.string(),
  login: z.string().nullable(),
  accountSize: z.number().nullable(),
  platformType: z.string(),
  phase: z.enum(["step1", "step2", "funded"]),
  online: z.boolean(),
  /** Pre-sleep snapshot the bot captured the last time this account's
   *  Brave was closed via operator-driven Sleep. null if there's never
   *  been a sleep on this account or if the snapshot capture failed. */
  lastSleepSnapshot: SleepSnapshotZ.nullable().optional(),
  lastSleepSnapshotAt: z.number().int().nullable().optional(),
  /** Operator-acknowledged firm rule percentages, sourced from the
   *  `accounts` row at handler time. Drive the Trading Growth Curve
   *  reference lines and the Trading Objectives progress bars on the
   *  detail page. All `0..100` (e.g. 5 = 5%). Null when the operator
   *  hasn't captured the firm's value yet. Optional so existing callers
   *  parsing legacy responses keep working. */
  profitTargetPct: z.number().nullable().optional(),
  maxDrawdownPct: z.number().nullable().optional(),
  dailyLossPct: z.number().nullable().optional(),
  consistencyRulePct: z.number().nullable().optional(),
});
export type AccountPairSide = z.infer<typeof AccountPairSideZ>;

/** Account-pair list item. One row per two-account hedge. */
export const AccountPairListItemZ = z.object({
  id: z.string(),
  slug: z.string(),
  firmPairMode: z.enum(["cross", "same"]),
  status: z.enum(["active", "paused", "closed"]),
  /** Only meaningful when status='closed'. true=archived (reopenable, pair_id kept), false=dissolved (irreversible). */
  archived: z.boolean(),
  longAccount: AccountPairSideZ,
  shortAccount: AccountPairSideZ,
  /** Symbols Haiku is authorized to open on this pair. Empty = no trades. */
  symbolAllowlist: z.array(z.string()),
  /** Canonical AI involvement at the parent level. Each child symbol
   *  pair inherits this unless it has its own non-null `modeOverride`.
   *  Driven by POST /api/account-pairs/:slug/ai-mode. */
  aiMode: PairModeZ,
  /** Operator kill-switch. true = no AI heartbeat fires for any child
   *  pair regardless of mode. Driven by POST /ai-paused. Orthogonal to
   *  `aiMode` so flipping pause off restores the configured mode. */
  aiPaused: z.boolean(),
  /** How many child `pairs` rows currently exist (active or paused). */
  activeSymbolPairs: z.number().int().nonnegative(),
  /** Sum of netPnl across all child symbol pairs. */
  netPnl: z.number(),
  /** Last audit_log timestamp across any child pair. null if never decided. */
  lastDecisionAt: z.number().nullable(),
  /** Oldest non-closed symbol pair id, or null when no pair exists yet.
   *  Used by the overview card to open a single WS subscription per
   *  account pair — `account:tick` fan-out covers the whole account. */
  firstSymbolPairId: z.string().nullable().optional(),
  /** Pair-level sleep state. The toggle UI bootstraps from this and
   *  then receives live updates via `pair:sleep-state` over the
   *  HedgePair socket. Optional so existing callers keep parsing. */
  sleep: z
    .object({
      mode: z.enum(["auto", "manual"]),
      manualState: z.enum(["awake", "asleep"]),
      currentState: z.enum(["awake", "asleep", "partial", "unknown"]),
      autoSleepProtected: z.boolean(),
    })
    .nullable()
    .optional(),
  createdAt: z.number(),
  closedAt: z.number().nullable(),
});
export type AccountPairListItem = z.infer<typeof AccountPairListItemZ>;

export const ListAccountPairsResZ = z.object({
  accountPairs: z.array(AccountPairListItemZ),
});
export type ListAccountPairsRes = z.infer<typeof ListAccountPairsResZ>;

/** Detail view — list item + embedded children. */
export const AccountPairDetailResZ = z.object({
  accountPair: AccountPairListItemZ,
  symbolPairs: z.array(PairListItemZ),
});
export type AccountPairDetailRes = z.infer<typeof AccountPairDetailResZ>;

export const CreateAccountPairReqZ = z.object({
  longAccountId: z.string().min(1).max(32),
  shortAccountId: z.string().min(1).max(32),
  firmPairMode: z.enum(["cross", "same"]).default("cross"),
  symbolAllowlist: z.array(z.string().min(1).max(16)).default([]),
});
export type CreateAccountPairReq = z.infer<typeof CreateAccountPairReqZ>;

export const UpdateAllowlistReqZ = z.object({
  symbolAllowlist: z.array(z.string().min(1).max(16)),
});
export type UpdateAllowlistReq = z.infer<typeof UpdateAllowlistReqZ>;

export const AddSymbolPairReqZ = z.object({
  symbol: z.string().min(1).max(16),
  lots: z.number().positive(),
  /** Captured from the preceding symbol-validate round-trip. Optional
   *  because some drivers can't reach the contract-size modal — when
   *  null, bot-side auto-sizing falls back to the target lots. */
  contractSize: z.number().positive().nullable().optional(),
  leverage: z.number().positive().nullable().optional(),
});
export type AddSymbolPairReq = z.infer<typeof AddSymbolPairReqZ>;

export const UpdatePairLotsReqZ = z.object({
  lots: z.number().positive().max(1000),
});
export type UpdatePairLotsReq = z.infer<typeof UpdatePairLotsReqZ>;

export const SetAccountPairAiModeReqZ = z.object({
  aiMode: PairModeZ,
});
export type SetAccountPairAiModeReq = z.infer<typeof SetAccountPairAiModeReqZ>;

export const SetAccountPairAiPausedReqZ = z.object({
  paused: z.boolean(),
});
export type SetAccountPairAiPausedReq = z.infer<typeof SetAccountPairAiPausedReqZ>;
